import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  FeatureFlagKey,
  FeatureFlags,
  FeatureFlagSource,
  communityFibreSurface,
  defaultFeatureFlags,
  featureFlagKeys,
  featureFlagsFromUrl,
  loadStoredFeatureFlags,
  resolveFeatureFlags,
  saveStoredFeatureFlags,
} from "./flags";

export {
  featureFlagDefinitions,
  featureFlagKeys,
  defaultFeatureFlags,
  featureFlagsFromUrl,
  isFlagEffective,
  communityFibreSurface,
} from "./flags";
export type {
  FeatureFlagKey, FeatureFlags, FeatureFlagSource, CommunityFibreSurface,
} from "./flags";

/**
 * Runtime feature flags (see flags.ts for the standard). Seeded once in
 * `App.tsx` from the saved overrides and the URL query string, then live in
 * the store. The /flags page toggles them and persists the toggles.
 */
export interface FeatureFlagsState {
  value: FeatureFlags;
  sources: Record<FeatureFlagKey, FeatureFlagSource>;
  /** The saved overrides, as persisted. */
  stored: Partial<FeatureFlags>;
  /** The URL's session-only overrides. */
  fromUrl: Partial<FeatureFlags>;
}

const initialState: FeatureFlagsState = {
  value: defaultFeatureFlags,
  sources: featureFlagKeys.reduce(
    (acc, key) => ({ ...acc, [key]: 'default' as FeatureFlagSource }),
    {} as Record<FeatureFlagKey, FeatureFlagSource>,
  ),
  stored: {},
  fromUrl: {},
};

const apply = (state: FeatureFlagsState) => {
  const resolved = resolveFeatureFlags(state.stored, state.fromUrl);
  state.value = resolved.value;
  state.sources = resolved.sources;
};

export const featureFlagsSlice = createSlice({
  name: "featureFlags",
  initialState,
  reducers: {
    // Called once at startup with the parsed URL query.
    initFeatureFlags: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.stored = loadStoredFeatureFlags();
      state.fromUrl = featureFlagsFromUrl(action.payload);
      apply(state);
    },
    // A toggle on the /flags page: saved for this browser. It also clears
    // the URL's say on that flag, so what the page shows is what applies.
    setFeatureFlag: (state, action: PayloadAction<{ key: FeatureFlagKey; value: boolean }>) => {
      const { key, value } = action.payload;
      if (value === defaultFeatureFlags[key]) {
        delete state.stored[key];
      } else {
        state.stored[key] = value;
      }
      delete state.fromUrl[key];
      saveStoredFeatureFlags(state.stored);
      apply(state);
    },
    // Back to the defaults, and nothing saved.
    resetFeatureFlags: (state) => {
      state.stored = {};
      state.fromUrl = {};
      saveStoredFeatureFlags({});
      apply(state);
    },
    // Session-only override, kept for callers that set several at once.
    setFeatureFlags: (state, action: PayloadAction<Partial<FeatureFlags>>) => {
      state.fromUrl = { ...state.fromUrl, ...action.payload };
      apply(state);
    },
  },
});

export const {
  initFeatureFlags, setFeatureFlag, resetFeatureFlags, setFeatureFlags,
} = featureFlagsSlice.actions;

export const selectFeatureFlags = (state: RootState) => state.featureFlags.value;
export const selectFeatureFlagSources = (state: RootState) => state.featureFlags.sources;
export const selectHideEveningSpeedToggle = (state: RootState) => state.featureFlags.value.hideEveningSpeedToggle;
export const selectPromoPricing = (state: RootState) => state.featureFlags.value.promoPricing;
export const selectPlanCardPoints = (state: RootState) => state.featureFlags.value.planCardPoints;
export const selectFreeTextDisclaimersEnabled = (state: RootState) => state.featureFlags.value.freeTextDisclaimers;
export const selectCommunityFibreEnabled = (state: RootState) => state.featureFlags.value.communityFibre;
/** mef or npis: the surface the Community Fibre calls name. */
export const selectCommunityFibreSurface = (state: RootState) => communityFibreSurface(state.featureFlags.value);

export default featureFlagsSlice.reducer;
